import z from 'zod';
import { FrequencyMode, FrequencyPeriod } from 'modules/frequencyManagement/lib/types';
import {
  MODE,
  CAPPING_PERIOD,
  CAPPING_MAX_ALLOWED,
  OPTIMIZE_PERIOD,
  OPTIMIZE_MIN_ALLOWED,
  OPTIMIZE_MAX_ALLOWED,
  MESSAGE_TYPES,
  ID,
  INDEX,
  NAME,
  CHANNEL_NAME,
  INITIAL_MODE,
} from './FormProvider.constants';
import { createRootName } from '../commonUtils';

const cappingMaxSchema = z
  .number({
    invalid_type_error: 'Number of messages is required',
    required_error: 'Number of messages is required',
  })
  .min(1, { message: 'Number of messages must be greater than 0 and less than 100' })
  .max(99, { message: 'Number of messages must be greater than 0 and less than 100' })
  .int();
const optimizeMinSchema = z
  .number({
    invalid_type_error: 'Min and max values required',
    required_error: 'Min and max values required',
  })
  .min(1, { message: 'Values must be between 1 and 99' })
  .max(98, { message: 'Values must be between 1 and 99' })
  .int();
const optimizeMaxSchema = z
  .number({
    invalid_type_error: 'Min and max values required',
    required_error: 'Min and max values required',
  })
  .min(2, { message: 'Values must be between 1 and 99' })
  .max(99, { message: 'Values must be between 1 and 99' })
  .int();

const offOrNoCapSchema = z.object({
  [MODE]: z.nativeEnum(FrequencyMode),
});

const cappingSchema = z.object({
  [CAPPING_PERIOD]: z.nativeEnum(FrequencyPeriod),
});

const baseInputFieldsSchema = z
  .object({
    [OPTIMIZE_PERIOD]: z.nativeEnum(FrequencyPeriod),
    [CAPPING_MAX_ALLOWED]: z.any(),
    [OPTIMIZE_MIN_ALLOWED]: optimizeMinSchema,
    [OPTIMIZE_MAX_ALLOWED]: optimizeMaxSchema,
  })
  .partial();

const allFrequencySchemas = offOrNoCapSchema.merge(baseInputFieldsSchema).merge(cappingSchema);

export const messageTypeMergedSchema = z
  .object({
    [INDEX]: z.number(), // Index is Client-side only
    [NAME]: z.string(), // Name is used for display and filtering only
    [CHANNEL_NAME]: z.string(),
    [ID]: z.number(),
    [INITIAL_MODE]: z.nativeEnum(FrequencyMode),
  })
  .merge(allFrequencySchemas)
  .partial();

export const schema = z
  .object({
    [MESSAGE_TYPES]: z.array(messageTypeMergedSchema).default([]),
  })
  .merge(allFrequencySchemas)
  .superRefine((allData: any, ctx) => {
    if (!allData) {
      return false;
    }

    const validateNumberFields = (messageData: any, messageTypeIndex?: number) => {
      const isMessageType = messageTypeIndex !== undefined;
      const rootName = createRootName(messageTypeIndex);

      const maxCappingPath = isMessageType
        ? `${rootName}.${CAPPING_MAX_ALLOWED}`
        : CAPPING_MAX_ALLOWED;
      const minAllowedPath = isMessageType
        ? `${rootName}.${OPTIMIZE_MIN_ALLOWED}`
        : OPTIMIZE_MIN_ALLOWED;
      const maxAllowedPath = isMessageType
        ? `${rootName}.${OPTIMIZE_MAX_ALLOWED}`
        : OPTIMIZE_MAX_ALLOWED;
      const maxCapping = messageData[CAPPING_MAX_ALLOWED];
      const minAllowed = messageData[OPTIMIZE_MIN_ALLOWED];
      const maxAllowed = messageData[OPTIMIZE_MAX_ALLOWED];
      const mode = messageData[MODE];
      if (mode === FrequencyMode.Cap) {
        try {
          cappingMaxSchema.parse(maxCapping);
        } catch (error: any) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: error.errors[0].message,
            path: [maxCappingPath],
          });
        }
      }

      if (mode === FrequencyMode.Optimize) {
        try {
          optimizeMinSchema.parse(minAllowed);
        } catch (error: any) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: error.errors[0].message,
            path: [minAllowedPath],
          });
        }
        try {
          optimizeMaxSchema.parse(maxAllowed);
        } catch (error: any) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: error.errors[0].message,
            path: [maxAllowedPath],
          });
        }

        if (
          typeof minAllowed === 'number' &&
          typeof maxAllowed === 'number' &&
          minAllowed >= maxAllowed
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Minimum cannot be greater than maximum',
            path: [minAllowedPath],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Minimum cannot be greater than maximum',
            path: [maxAllowedPath],
          });
        }
      }
    };

    validateNumberFields(allData);
    if (allData[MESSAGE_TYPES] && allData[MESSAGE_TYPES].length > 0) {
      allData[MESSAGE_TYPES].forEach((messageType: any, index: number) =>
        validateNumberFields(messageType, index),
      );
    }
    return z.NEVER;
  });
